'use client';

import { ElementProps } from '@/components/sections/builder/types';
import { cn, normalizePinpoint } from '@/lib/utils';
import { HttpTypes } from '@/types';
import { CheckoutFormElemetDataType } from '@/components/sections/builder/elements/body/checkout-form/type';
import { dummyCarts } from '@/constant/dummyData/dummy-carts';
import { BaseShippingRatesPayload, TExternalOrder } from '@/types/http/orders/common';
import React from 'react';
import { IAreas } from '@/types/area';
import { BaseCart } from '@/types/http/cart/common';
import Cookies from 'js-cookie';
import { useQuery } from '@tanstack/react-query';
import { retrieveCustomerDetail, retrieveListCustomerAddress } from '@/lib/actions/customer/action';
import { CheckoutItem } from '@/components/sections/builder/elements/body/checkout-form/component/checkout-item';
import { CheckoutForm } from '@/components/sections/builder/elements/body/checkout-form/component/checkout-form';
import LoadingCheckoutPage from '@/components/sections/builder/elements/body/checkout-form/component/loading-checkout-page';
import { BaseCustomerAddress } from '@/types/http/customer/common';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { useCustomerStore } from '@/stores/customer';
import { shallow } from 'zustand/shallow';
import { useBuilderStore } from '@/stores/builder';

const calculateTotalWeight = (carts?: HttpTypes.StoreCart) =>
  carts?.items?.reduce((acc, item) => acc + Number(item.item_weight) * item.quantity, 0);

const prepareOrderItems = (carts?: HttpTypes.StoreCart) =>
  carts?.items?.map((item: BaseCart) => ({
    item_id: item.id,
    qty: item.quantity,
    discount: 0,
    tax: 0,
    price: item.sell_price,
    total: item.sell_price * item.quantity,
  }));

const prepareShippingPayload = (
  area: string,
  zipcode: string,
  totalValue: number,
  totalWeight: number,
  carts: BaseCart[]
): BaseShippingRatesPayload => ({
  destination: {
    area_id: area,
    zipcode: zipcode,
  },
  weight: totalWeight,
  service_category_id: 1,
  total_value: totalValue,
  items: carts?.map((item: BaseCart) => ({
    item_name: item.product_name,
    category: [],
    weight: String(item.item_weight),
    length: String(item.item_length),
    width: String(item.item_width),
    height: String(item.item_height),
    quantity: item.quantity,
    value: String(item.subtotal),
  })),
});

export default function ElementContent({
  element,
  data,
  host,
  preview,
  isLoadingData,
  storeSetting,
  theme,
}: ElementProps<HttpTypes.StoreCartDetail>) {
  const token = Cookies.get('accessToken') as string;
  const content = element.data as CheckoutFormElemetDataType;
  const eventRef = React.useRef(false);

  const customer = useCustomerStore((state) => state.customer, shallow);

  const initialValues = React.useMemo(
    () => ({
      customer: {
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        area: customer.area_id || '',
        area_name: customer.area || '',
        address: customer.address || '',
        zipcode: '',
        province: '',
        country: 'indonesia',
      },
      shipping_info: {
        shipping_province: '',
        shipping_province_id: '',
        shipping_city: '',
        shipping_city_id: '',
        shipping_district_id: '',
        shipping_area: '',
        shipping_subdistrict_id: '',
        shipping_subdistrict: '',
        shipping_post_code: '',
        shipping_address: '',
        shipping_service_id: '',
        shipping_first_name: '',
        shipping_last_name: '',
        shipping_phone: '',
        shipping_coordinate: {
          lat: '',
          long: '',
        },
      },
    }),
    [customer]
  );

  const [formValues, setFormValues] = React.useState<TExternalOrder>(initialValues);
  const [address, setAddress] = React.useState<{
    area: string;
    area_name: string;
    address: string;
    zipcode: string;
    pinpoint: { lat: string | number; long: string | number };
  }>({
    area: '',
    area_name: '',
    address: '',
    zipcode: '',
    pinpoint: { lat: '', long: '' },
  });
  const [areaData, setAreaData] = React.useState<IAreas | null>(null);
  const [pinpoint, setPinpoint] = React.useState<{ lat: string; long: string } | null>({
    lat: address?.pinpoint?.lat as string,
    long: address?.pinpoint?.long as string,
  });
  const [dataShipping, setDataShipping] = React.useState<BaseShippingRatesPayload | null>(null);
  const [isLoadingSelectShipping, setIsLoadingSelectShipping] = React.useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = React.useState<string>('online');
  const [isInsurance, setIsInsurance] = React.useState<boolean>(false);
  const [selectedOption, setSelectedOption] = React.useState<'delivery' | 'pickup_in_store'>(
    'delivery'
  );

  const themeBuilder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );

  const cartTheme = preview ? themeBuilder.theme.cart : theme?.cart;
  const promotionTheme = preview ? themeBuilder.theme.promotion : theme?.promotion;

  const { data: dataProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['customer-profile-checkout'],
    queryFn: async () => await retrieveCustomerDetail(host as string, token as string),
    enabled: !!token,
    select: (res) => res.data,
  });

  const { data: listAddress, isLoading: isLoadingAddress } = useQuery({
    queryKey: ['customer-list-address-checkout'],
    queryFn: async () => await retrieveListCustomerAddress(host as string, token as string),
    enabled: !!token,
    select: (res) => res.data,
  });

  const defaultAddress = React.useMemo(() => listAddress?.find((i) => i.is_default), [listAddress]);

  const carts = preview ? dummyCarts : data;
  const totalWeight = React.useMemo(
    () => calculateTotalWeight(carts as HttpTypes.StoreCart),
    [carts]
  );

  const subtotal = carts?.subtotal ?? 0;
  const discount = carts?.discount_amount ?? 0;
  const tax = carts?.tax_amount ?? 0;
  const discountTransaction = carts?.total_promotion_discount ?? 0;

  const totalValue = subtotal + tax - (discount + discountTransaction);

  const handleChangeAddress = React.useCallback(
    (newAddress: BaseCustomerAddress) => {
      const parsedPinpoint = normalizePinpoint(newAddress.pinpoint);

      setAddress({
        area: newAddress.area,
        area_name: newAddress.area_name as string,
        address: newAddress.address,
        zipcode: newAddress.zipcode,
        pinpoint: {
          lat: parsedPinpoint?.lat as number,
          long: parsedPinpoint?.lng as number,
        },
      });

      setPinpoint({
        lat: String(parsedPinpoint?.lat),
        long: String(parsedPinpoint?.lng),
      });

      setFormValues((prev) => ({
        ...prev,
        customer: {
          ...prev.customer,
          first_name: newAddress.first_name,
          last_name: newAddress.last_name as string,
          email: newAddress.email as string,
          phone: newAddress.phone,
          area: newAddress.area,
          area_name: newAddress.area_name as string,
          address: newAddress.address,
          zipcode: newAddress.zipcode,
          pinpoint: {
            lat: String(parsedPinpoint?.lat),
            long: String(parsedPinpoint?.lng),
          },
        },
      }));

      setAreaData({
        name: newAddress.area_name as string,
        province: newAddress.area_name?.split(', ').pop()?.split(' ')[0] || '',
        city: newAddress.area_name?.split(', ')[2] || '',
        district: newAddress.area_name?.split(', ')[1] || '',
        area: (newAddress.area as string) || customer.area_id,
        area_id: (newAddress.area as string) || customer.area_id,
        zipcode: newAddress.zipcode as string,
      });

      setDataShipping(
        prepareShippingPayload(
          newAddress.area,
          newAddress.zipcode,
          totalValue,
          totalWeight as number,
          carts?.items as BaseCart[]
        )
      );
    },
    [carts]
  );

  React.useEffect(() => {
    // if (!dataProfile || !defaultAddress) return;

    const email = dataProfile?.email || customer.email || '';
    const firstName = defaultAddress?.first_name || customer.first_name || '';
    const lastName = defaultAddress?.last_name || customer.last_name || '';
    const phone = defaultAddress?.phone || customer.phone || '';
    const area = defaultAddress?.area || customer.area_id || '';
    const areaName = defaultAddress?.area_name || customer.area || '';
    const addressDefault = defaultAddress?.address || customer.address || '';
    const zipcode = defaultAddress?.zipcode || '';
    const pinpoint = defaultAddress?.pinpoint;
    const parsedPinpoint = normalizePinpoint(pinpoint);

    setFormValues(
      (prev) =>
        ({
          ...prev,
          customer: {
            ...prev.customer,
            first_name: firstName,
            last_name: lastName,
            email,
            phone,
            area: area,
            area_name: areaName,
            address: addressDefault,
            zipcode,
            pinpoint: parsedPinpoint,
          },
          order_items: prepareOrderItems(carts as HttpTypes.StoreCart) || [],
          discount_type: 'fixed',
          grand_total: carts?.grand_total,
        } as unknown as TExternalOrder)
    );

    setPinpoint({
      lat: String(parsedPinpoint?.lat || ''),
      long: String(parsedPinpoint?.lng || ''),
    });

    setAddress({
      area: address.area ?? area,
      area_name: address.area_name ?? areaName,
      address: addressDefault,
      zipcode,
      pinpoint: parsedPinpoint as unknown as { lat: string; long: string },
    });

    setAreaData({
      name: areaName as string,
      province: areaName?.split(', ').pop()?.split(' ')[0] || '',
      city: areaName?.split(', ')[2] || '',
      district: areaName?.split(', ')[1] || '',
      area: area || customer.area_id,
      area_id: area || customer.area_id,
      zipcode: zipcode as string,
    });

    setDataShipping(
      prepareShippingPayload(
        area as string,
        zipcode as string,
        totalValue as number,
        totalWeight as number,
        carts?.items as BaseCart[]
      )
    );
  }, [dataProfile, defaultAddress]);

  React.useEffect(() => {
    setDataShipping(
      prepareShippingPayload(
        areaData?.area_id as string,
        areaData?.zipcode as string,
        totalValue as number,
        totalWeight as number,
        carts?.items as BaseCart[]
      )
    );
  }, [areaData, carts?.items?.length]);

  React.useEffect(() => {
    if (eventRef.current || preview || !carts) return;

    SendEventAnalytic('begin_checkout', {
      currency: 'IDR',
      value: carts?.grand_total,
      items: carts.items?.map((item) => ({
        item_id: item.id || '',
        item_name: item.product_name || '',
      })),
    });

    SendEventMetapixel('InitiateCheckout', {
      content_ids: carts.items?.map((item) => item.id) || [],
      contents: carts.items?.map((item) => ({
        id: item.id || '',
        quantity: item.quantity || 0,
      })),
      num_items: carts.items?.length,
      content_type: 'checkout',
      value: carts.grand_total,
      currency: 'IDR',
    });

    eventRef.current = true;
  }, [carts, preview]);

  if (isLoadingProfile || isLoadingAddress || isLoadingData)
    return (
      <LoadingCheckoutPage
        bgFirst={content.colorSchemeForm?.background}
        bgSecond={content.colorSchemeItem?.background}
      />
    );

  return (
    <>
      <section className='theme_layout_page_width mx-auto w-full'>
        <div
          className={cn(
            'flex w-full gap-2 transition-all duration-300',
            content.layoutPosition === 'left' && 'flex-col md:flex-row',
            content.layoutPosition === 'right' && 'flex-col-reverse md:flex-row-reverse'
          )}
        >
          {/* Checkout Form */}
          <CheckoutForm
            host={host as string}
            content={content}
            isPreview={preview}
            isLoadingSelectShipping={isLoadingSelectShipping}
            isInsurance={isInsurance}
            formValues={formValues}
            address={{
              ...address,
              pinpoint: {
                lat: String(address.pinpoint?.lat || ''),
                long: String(address.pinpoint?.long || ''),
              },
            }}
            pinpoint={pinpoint as { lat: string; long: string }}
            setPinpoint={
              setPinpoint as React.Dispatch<React.SetStateAction<{ lat: string; long: string }>>
            }
            paymentMethod={paymentMethod}
            carts={carts as HttpTypes.StoreCartDetail}
            areaData={areaData as IAreas}
            dataShipping={dataShipping as BaseShippingRatesPayload}
            dataProfile={dataProfile}
            listAddress={listAddress as BaseCustomerAddress[]}
            setAreaData={setAreaData}
            setIsLoadingSelectShipping={setIsLoadingSelectShipping}
            setPaymentMethod={setPaymentMethod}
            setAddress={setAddress}
            setIsInsurance={setIsInsurance}
            handleChangeAddress={handleChangeAddress}
            storeSetting={storeSetting}
            promotionTheme={promotionTheme}
            cartTheme={cartTheme}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            theme={theme}
          />

          {/* Desktop Checkout Item */}
          <CheckoutItem
            className='hidden md:flex'
            content={content}
            carts={carts as HttpTypes.StoreCartDetail}
            isShippingInsurance={isInsurance}
            isLoadingSelectShipping={isLoadingSelectShipping}
            promotionTheme={promotionTheme}
            selectedOption={selectedOption}
          />
        </div>
      </section>
    </>
  );
}
